import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UserSettings } from '@mailpilot/shared';
import { api, type ResOf } from '../lib/api';
import { qk } from '../lib/keys';
import { useToast } from '../components/ui/Toast';

export type Me = ResOf<'GET /api/auth/me'>;

export function useMe() {
  return useQuery({ queryKey: qk.me, queryFn: () => api('GET /api/auth/me'), staleTime: 60_000 });
}

/** Current settings (only valid inside authenticated routes). */
export function useSettings(): UserSettings | undefined {
  return useMe().data?.settings;
}

type SettingsPatch = Partial<UserSettings>;

/** PATCH /api/settings with optimistic update. */
export function useUpdateSettings() {
  const qc = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: (patch: SettingsPatch) => api('PATCH /api/settings', { body: patch }),
    onMutate: async (patch) => {
      await qc.cancelQueries({ queryKey: qk.me });
      const prev = qc.getQueryData<Me>(qk.me);
      if (prev) {
        const s = prev.settings;
        qc.setQueryData<Me>(qk.me, {
          ...prev,
          settings: {
            ...s,
            ...patch,
            ring: { ...s.ring, ...(patch.ring ?? {}) },
            quietHours: { ...s.quietHours, ...(patch.quietHours ?? {}) },
            ai: { ...s.ai, ...(patch.ai ?? {}) },
          },
        });
      }
      return { prev };
    },
    onError: (err, _p, c) => {
      if (c?.prev) qc.setQueryData(qk.me, c.prev);
      toast.error(err);
    },
    onSuccess: (res) => {
      qc.setQueryData<Me>(qk.me, (prev) => (prev ? { ...prev, settings: res.settings } : prev));
      void window.mailpilotDesktop?.refreshSettings().catch(() => undefined);
    },
  });
}
